import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "@emotion/styled";
import {
  text,
  surface,
  border,
  fontSize,
  spacing,
  borderRadius,
  shadow,
  zIndex as zIndexTokens,
  lineHeight,
} from "@atlas-int/tokens";

/* ------------------------------------------------------------------ */
/*  Info icon (small circled "i")                                      */
/* ------------------------------------------------------------------ */

const InfoIconSvg = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle
      cx="8"
      cy="8"
      r="7"
      stroke="currentColor"
      strokeWidth="1.5"
      opacity="0.5"
    />
    <text
      x="8"
      y="12"
      textAnchor="middle"
      fill="currentColor"
      fontSize="10"
      fontWeight="600"
      fontFamily="sans-serif"
      style={{ textTransform: "none" }}
      opacity="0.5"
    >
      i
    </text>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Styled pieces                                                      */
/* ------------------------------------------------------------------ */

const Trigger = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: help;
`;

const LabelText = styled.span`
  cursor: help;
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: ${spacing[1]};
  flex-shrink: 0;
`;

const TooltipBubble = styled.div`
  position: fixed;
  background: ${surface.white};
  color: ${text.primary};
  font-size: ${fontSize.xs};
  font-weight: 400;
  line-height: ${lineHeight.normal};
  text-transform: none;
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius.base};
  box-shadow: ${shadow.sm};
  border: 1px solid ${border.subtle};
  white-space: normal;
  width: max-content;
  max-width: 260px;
  z-index: ${zIndexTokens.overlay};
  pointer-events: none;
  box-sizing: border-box;
`;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export interface InfoTooltipProps {
  /** Tooltip explanation text (or any ReactNode). */
  text: React.ReactNode;
  /**
   * Optional label rendered before the info icon.
   * When provided the entire label + icon region acts as the hover trigger.
   * When omitted only the icon is shown.
   */
  label?: React.ReactNode;
  /** className pass-through for Emotion composition */
  className?: string;
  /** Override the default max-width of the tooltip bubble (default: 260px). */
  maxWidth?: number | string;
  /** Place the tooltip below the trigger instead of above (default: "above"). */
  placement?: "above" | "below";
}

/**
 * Displays a small circled "i" icon that reveals an explanatory tooltip on
 * hover. Optionally wraps a text label so the entire label + icon is the
 * trigger area with a `cursor: help` ("?") pointer.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  label,
  className,
  maxWidth,
  placement = "above",
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      top: placement === "below" ? rect.bottom + 6 : rect.top - 6,
      left: rect.left + rect.width / 2,
    });
  }, [placement]);

  const handleEnter = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const handleLeave = useCallback(() => {
    setVisible(false);
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
  }, [visible, updatePosition]);

  return (
    <>
      <Trigger
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className={className}
      >
        {label !== undefined && <LabelText>{label}</LabelText>}
        <IconSlot>
          <InfoIconSvg />
        </IconSlot>
      </Trigger>
      {visible &&
        createPortal(
          <TooltipBubble
            style={{
              top: pos.top,
              left: pos.left,
              transform:
                placement === "below"
                  ? "translateX(-50%)"
                  : "translate(-50%, -100%)",
              ...(maxWidth !== undefined && {
                maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
              }),
            }}
          >
            {text}
          </TooltipBubble>,
          document.body,
        )}
    </>
  );
};

export default InfoTooltip;
